/**
 * Score → grade scale bar helpers.
 * The bar is a plain 0点〜満点 axis, so every segment width and every marker position is the
 * score's real share of 満点 — nothing is drawn with fixed ratios.
 */
import { TMockExamGrade } from "@/infrastructure/adapters/mock-exam/mock-exam.types";
import { GRADE_LOWER_BOUND_RATIO } from "@/features/mock-exam/components/grade-guide/grade-guide.constants";

const clampPercent = (value: number): number => Math.min(100, Math.max(0, value));

/**
 * Scale bar segments from 0点 to 満点 (lowest grade first), sized by their real share of 満点.
 * With a 70% passing line: E 0〜35%, D 35〜49%, C 49〜63%, B 63〜70%, A 70〜100%.
 */
export const getGradeScaleSegments = (passingScorePercentage: number): { grade: TMockExamGrade; widthPercent: number }[] => {
  const passingPercent = clampPercent(passingScorePercentage);

  return GRADE_LOWER_BOUND_RATIO.map(({ grade, ratio }, index) => {
    const nextBound = GRADE_LOWER_BOUND_RATIO[index + 1];

    return {
      grade,
      // The topmost grade has no bound above it, so it runs up to 満点.
      widthPercent: (nextBound ? nextBound.ratio * passingPercent : 100) - ratio * passingPercent,
    };
  }).filter(({ widthPercent }) => widthPercent > 0);
};

/** Horizontal position (%) of a score on the scale bar — its plain share of 満点. */
export const getScalePositionPercent = (earnedScore: number, totalScore: number): number => {
  if (totalScore <= 0) return 0;

  return clampPercent((earnedScore / totalScore) * 100);
};
